import React, { Component, useEffect } from "react";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { HelmetProvider } from "react-helmet-async";
import { StoreProvider } from "./context/StoreContext";
import { Layout } from "./components/Layout";
import Home from "./pages/Home";
import Shop from "./pages/Shop";
import ProductDetail from "./pages/ProductDetail";
import CartPage from "./pages/CartPage";
import CheckoutPage from "./pages/CheckoutPage";
import PaymentResult from "./pages/PaymentResult";
import MeetRachel from "./pages/MeetRachel";
import CustomOrdersPage from "./pages/CustomOrdersPage";
import Contact from "./pages/Contact";
import InfoPage from "./pages/InfoPage";
import Wishlist from "./pages/Wishlist";
import NotFound from "./pages/NotFound";
import AdminApp from "./pages/admin/AdminApp";

class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-8 text-center bg-candy-mist">
          <h1 className="font-display text-3xl text-ink">Something unraveled</h1>
          <p className="text-muted">An unexpected error occurred. Please refresh the page.</p>
          <button onClick={() => window.location.reload()} data-testid="error-reload-button" className="px-6 py-3 rounded-full bg-candy text-white font-bold">Refresh</button>
        </div>
      );
    }
    return this.props.children;
  }
}

function SmoothScroll() {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const lenis = new Lenis({ duration: 1.05, smoothWheel: true });
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);
  return null;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

export default function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
      <StoreProvider>
        <BrowserRouter>
          <SmoothScroll />
          <ScrollToTop />
          <Routes>
            <Route path="/admin" element={<AdminApp />} />
            <Route path="/admin/:section" element={<AdminApp />} />
            <Route element={<Layout />}>
              <Route path="/" element={<Home />} />
              <Route path="/shop" element={<Shop />} />
              <Route path="/shop/:collection" element={<Shop />} />
              <Route path="/product/:slug" element={<ProductDetail />} />
              <Route path="/cart" element={<CartPage />} />
              <Route path="/checkout" element={<CheckoutPage />} />
              <Route path="/payment/success" element={<PaymentResult success />} />
              <Route path="/payment/cancel" element={<PaymentResult />} />
              <Route path="/meet-rachel" element={<MeetRachel />} />
              <Route path="/custom-orders" element={<CustomOrdersPage />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/faq" element={<InfoPage slug="faq" />} />
              <Route path="/care-guide" element={<InfoPage slug="care-guide" />} />
              <Route path="/shipping-returns" element={<InfoPage slug="shipping-returns" />} />
              <Route path="/privacy" element={<InfoPage slug="privacy" />} />
              <Route path="/terms" element={<InfoPage slug="terms" />} />
              <Route path="/accessibility" element={<InfoPage slug="accessibility" />} />
              <Route path="/wishlist" element={<Wishlist />} />
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </StoreProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}
